import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';

import { SimpleDropdown } from '../../../../../components/simple-dropdown/index';
import { emptyArray } from 'ui-components/default-props/empty';
import { useFulfillmentPlannerContext } from '../../../../../fulfillment-planner/context';
import { serviceLevel } from '../../../../../services/other-shipping-options/constants';
import Global from '../../../../../store/global-selectors';
import { messages } from './messages';

/**
 * Dropdown component for selecting a service level for "other shipping options".
 * @param {object} props - Component props.
 * @param {string|null} [props.optionData] - Currently selected service level code, or null when not set.
 * @param {(option: { id: string, value: string }) => void} [props.updateOption] - Callback invoked when the selected service level changes.
 * @param {string|null} [props.sellerLabelProviderId] - Optional seller label provider ID. If provided, takes precedence over context value.
 * @returns {JSX.Element} The rendered service level dropdown.
 */
export const ServiceLevelDropdown = ({
  optionData = null,
  updateOption = noop,
  sellerLabelProviderId: sellerLabelProviderIdProp = null,
}) => {
  const intl = useIntl();
  const serviceLevels = useSelector(Global.selectServiceLevelsData);
  const contextData = useFulfillmentPlannerContext();
  const sellerLabelProviderIdContext = contextData.serviceIdAndSellerLabelProviderId?.sellerLabelProviderId;

  // Prefer prop over context (for bulk update scenarios where context is not available)
  const sellerLabelProviderIdString = sellerLabelProviderIdProp ?? sellerLabelProviderIdContext;
  const sellerLabelProviderId = Number.parseInt(sellerLabelProviderIdString, 10);

  const options = serviceLevels.find(({ sellerProviderId }) => sellerProviderId === sellerLabelProviderId)?.serviceLevels || emptyArray;
  const optionsWithPlaceholder = [{ code: '', label: intl.formatMessage(messages.defaultTitle) }, ...options];

  const onChange = (value) => {
    updateOption({
      id: serviceLevel,
      value: value || '',
    });
  };

  return (
    <SimpleDropdown
      defaultTitle={intl.formatMessage(messages.defaultTitle)}
      dropdownMenuShouldHaveWidthOfButton
      fullWidth
      items={optionsWithPlaceholder.map(({ code, label }) => ({
        key: code,
        name: code || label,
      }))}
      onItemSelected={onChange}
      selectedItemKey={optionData || ''}
    />
  );
};
